// abort-challenge.ts
//
// `abort [--confirm <id>]` two-step challenge (spec §8/Req 7, tests 32-34).
// Step 1 issues a challenge with a TTL (`abort_challenge_ttl_sec`, §18).
// Step 2 (`abort confirm <id>`) only succeeds when: same actor as issued the
// challenge, same mission revision, and within the TTL window. This is
// intentionally SEPARATE from gate-registry (different identity: a challenge
// id, not a gate id) but shares the same "don't silently coerce" posture.

import { randomUUID } from "node:crypto";
import { join } from "node:path";
import { pathExists, readJson } from "../filesystem-helpers.js";
import { writeFileDurable } from "./durable-file-writer.js";

export interface AbortChallenge {
  challenge_id: string;
  mission_id: string;
  actor_id: string;
  mission_revision: number;
  issued_at: string;
  expires_at: string;
}

export class AbortConfirmRejectedError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "AbortConfirmRejectedError";
  }
}

// (code-review phase-04) throws SYNCHRONOUSLY (assertWellFormedAbortChallengeId
// below) — every current caller is an `async function`, so the throw becomes
// a rejected Promise automatically. A future caller invoking this from
// non-async code without a try/catch would crash instead of rejecting.
function challengePath(controlRoot: string, challengeId: string): string {
  // [N2] Every caller (CLI pre-check, halt-step, startup pass) reaches a
  // challenge file ONLY through this function — validating the id format
  // HERE, before any join/read/rm, is the one place that blocks `../../x`
  // path traversal for every current and future consumer at once (DRY).
  assertWellFormedAbortChallengeId(challengeId);
  return join(controlRoot, "abort-challenges", `${challengeId}.json`);
}

/** [N2] `issueAbortChallenge` always mints `A-<8 lowercase hex>` — anything else is rejected before it can reach a filesystem path. */
export function assertWellFormedAbortChallengeId(id: string): void {
  if (!/^A-[0-9a-f]{8}$/.test(id)) {
    throw new AbortConfirmRejectedError(`Challenge id "${id}" sai định dạng (cần A-xxxxxxxx).`);
  }
}

export interface IssueAbortChallengeInput {
  controlRoot: string;
  missionId: string;
  actorId: string;
  missionRevision: number;
  ttlSec: number;
  now?: () => Date;
}

/** Step 1: issue a challenge with a TTL. Returns the challenge (carries the id + expiry). */
export async function issueAbortChallenge(
  input: IssueAbortChallengeInput,
): Promise<AbortChallenge> {
  const now = input.now ?? (() => new Date());
  const issuedAt = now();
  const challenge: AbortChallenge = {
    challenge_id: `A-${randomUUID().slice(0, 8)}`,
    mission_id: input.missionId,
    actor_id: input.actorId,
    mission_revision: input.missionRevision,
    issued_at: issuedAt.toISOString(),
    expires_at: new Date(issuedAt.getTime() + input.ttlSec * 1000).toISOString(),
  };
  const path = challengePath(input.controlRoot, challenge.challenge_id);
  const { ensureDir } = await import("../filesystem-helpers.js");
  await ensureDir(join(input.controlRoot, "abort-challenges"));
  await writeFileDurable(path, Buffer.from(`${JSON.stringify(challenge, null, 2)}\n`, "utf8"));
  return challenge;
}

export interface ConfirmAbortChallengeInput {
  controlRoot: string;
  challengeId: string;
  actorId: string;
  missionRevision: number;
  now?: () => Date;
}

/**
 * Step 2: confirm. Rejects (AbortConfirmRejectedError) when the challenge is
 * missing, the actor differs from the one that issued it, the mission
 * revision has moved on, or the TTL has expired.
 */
export async function confirmAbortChallenge(
  input: ConfirmAbortChallengeInput,
): Promise<AbortChallenge> {
  const now = input.now ?? (() => new Date());
  const path = challengePath(input.controlRoot, input.challengeId);
  if (!(await pathExists(path))) {
    throw new AbortConfirmRejectedError(
      `Challenge "${input.challengeId}" không tồn tại hoặc đã dùng.`,
    );
  }
  const challenge = await readJson<AbortChallenge>(path);

  if (challenge.actor_id !== input.actorId) {
    throw new AbortConfirmRejectedError(
      `Actor "${input.actorId}" khác actor đã khởi tạo challenge ("${challenge.actor_id}").`,
    );
  }
  if (challenge.mission_revision !== input.missionRevision) {
    throw new AbortConfirmRejectedError(
      `Mission revision đã đổi (challenge tại revision ${challenge.mission_revision}, hiện tại ${input.missionRevision}) — challenge không còn hiệu lực.`,
    );
  }
  if (now().getTime() > Date.parse(challenge.expires_at)) {
    throw new AbortConfirmRejectedError(
      `Challenge "${input.challengeId}" đã hết hạn lúc ${challenge.expires_at}.`,
    );
  }
  return challenge;
}

/** [N9] Read-only lookup for the halt-step's actor check on a CLI-issued `abort` directive. Returns undefined when missing (never throws on a well-formed but absent id — that is a normal "no such challenge" case, not a rejection). */
export async function readAbortChallenge(
  controlRoot: string,
  challengeId: string,
): Promise<AbortChallenge | undefined> {
  const path = challengePath(controlRoot, challengeId);
  if (!(await pathExists(path))) return undefined;
  return readJson<AbortChallenge>(path);
}

/** Step 2 success side-effect: a challenge is single-use — the caller commits ABORTED and marks the directive processed BEFORE calling this (plan.md Risk Assessment ordering). `force: true` — already-gone is not an error. */
export async function deleteAbortChallenge(
  controlRoot: string,
  challengeId: string,
): Promise<void> {
  const { promises: fs } = await import("node:fs");
  await fs.rm(challengePath(controlRoot, challengeId), { force: true });
}
